import { Modal, TextArea, TextInput } from "@wfp/ui"
import useCommon from "hooks/useCommon"
import { type ReactNode, useEffect, useState } from "react"
import Select, { MultiValue } from "react-select"
import {
  CONTRACT_TYPE_LABELS,
  Contract as BackendContract,
  ContractType,
  formValuesToContractCreate,
  formValuesToPatchedContract,
  useCreateContractMutation,
  useListContractTypesQuery,
  useListCountriesQuery,
  usePatchContractMutation,
} from "services/aimsBackend"

type Option = {
  value: string
  label: string
}

const fallbackContractTypeOptions: Option[] = Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}))

const YEAR_REGEX = /^(19|20|21)\d{2}$/

type ContractFormValues = {
  name: string
  owner: string
  countries: Option[]
  contractTypes: Option[]
  subscriptionYear: string
}

const getContractFormErrors = ({
  name,
  owner,
  countries,
  contractTypes,
  subscriptionYear,
}: ContractFormValues): Record<string, string> => {
  const errors: Record<string, string> = {}
  if (!name.trim()) errors.name = "Name is required"
  if (!owner.trim()) errors.owner = "Owner is required"
  if (countries.length === 0) errors.countries = "Select at least one country or territory"
  if (contractTypes.length === 0) errors.contractTypes = "Select at least one contract type"
  if (!subscriptionYear.trim()) {
    errors.subscriptionYear = "Subscription year is required"
  } else if (!YEAR_REGEX.test(subscriptionYear.trim())) {
    errors.subscriptionYear = "Enter a valid 4-digit year (e.g. 2024)"
  }
  return errors
}

function RequiredFieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="wfp--label">
      {children}
      <span aria-label="required" style={{ color: "#da1e28" }}>
        {" "}
        *
      </span>
    </label>
  )
}

type Props = {
  open: boolean
  onClose: () => void
  editContract?: BackendContract | null
  onSaved?: (contract: BackendContract) => void
}

function toOptions(items: unknown[] | undefined, fallback: Option[] = []): Option[] {
  if (!items?.length) return fallback
  return items.map((it: any) => {
    const value = String(it.value ?? it.code ?? it.id ?? it.label ?? it.name ?? it)
    const label = String(it.label ?? it.name ?? it.title ?? value)
    return { value, label }
  })
}

export default function ContractFormModal({ open, onClose, editContract, onSaved }: Props) {
  const { showSnackBar } = useCommon()

  const isEditMode = !!editContract

  const [name, setName] = useState("")
  const [owner, setOwner] = useState("")
  const [countries, setCountries] = useState<Option[]>([])
  const [contractTypes, setContractTypes] = useState<Option[]>([])
  const [subscriptionYear, setSubscriptionYear] = useState("")
  const [description, setDescription] = useState("")
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Partial<Record<keyof ContractFormValues, boolean>>>({})

  const { data: contractTypesList = [] } = useListContractTypesQuery()
  const { data: countriesList = [] } = useListCountriesQuery()
  const [createContract, { isLoading: isCreating }] = useCreateContractMutation()
  const [patchContract, { isLoading: isPatching }] = usePatchContractMutation()
  const isSaving = isCreating || isPatching
  const hasConnectedAssets = isEditMode && (editContract?.assets?.length ?? 0) > 0
  const formErrors = getContractFormErrors({ name, owner, countries, contractTypes, subscriptionYear })
  const isFormValid = Object.keys(formErrors).length === 0
  const isSubmitDisabled = isSaving || !isFormValid
  const displayedError = (field: keyof ContractFormValues) => errors[field] || (touched[field] ? formErrors[field] : "")
  const markTouched = (field: keyof ContractFormValues) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  // Country options come from /countries/. Contract.countries[] stores iso3
  // codes per the backend's natural-key convention.
  const countryOptions = countriesList.map(c => ({ value: c.iso3, label: c.name }))
  const contractTypeOptions = toOptions(contractTypesList, fallbackContractTypeOptions).map(opt => ({
    value: opt.value,
    label: CONTRACT_TYPE_LABELS[opt.value as ContractType] ?? opt.label,
  }))

  // Reset form when modal opens or editContract changes
  useEffect(() => {
    if (!open) return
    setName(editContract?.name || "")
    setOwner(editContract?.owner || "")
    setCountries(editContract?.countries?.map(c => ({ value: c, label: c })) || [])
    setContractTypes(
      editContract?.contract_types?.map(t => ({
        value: t,
        label: CONTRACT_TYPE_LABELS[t as ContractType] ?? t,
      })) || [],
    )
    setSubscriptionYear(editContract?.subscription_year ? String(editContract.subscription_year) : "")
    setDescription(editContract?.description || "")
    setErrors({})
    setTouched({})
  }, [open, editContract])

  const validate = (): boolean => {
    setErrors(formErrors)
    return isFormValid
  }

  const handleSubmit = async () => {
    if (isSubmitDisabled) return
    if (!validate()) return

    const formValues = {
      name: name.trim(),
      owner: owner.trim(),
      countries: countries.map(c => c.value),
      contractTypes: contractTypes.map(ct => ct.value as ContractType),
      subscriptionYear: Number(subscriptionYear.trim()),
      description: description.trim(),
      assets: editContract?.assets ?? [],
    }

    try {
      if (isEditMode && editContract) {
        const updated = await patchContract({
          id: editContract.id,
          body: formValuesToPatchedContract(formValues),
        }).unwrap()
        showSnackBar("Contract updated successfully!", "success")
        onSaved?.(updated)
      } else {
        const created = await createContract(formValuesToContractCreate(formValues)).unwrap()
        showSnackBar("Contract created successfully!", "success")
        onSaved?.(created)
      }
      onClose()
    } catch (err: any) {
      const data = err?.data
      const backendFieldMap: Record<string, string> = {
        name: "name",
        owner: "owner",
        countries: "countries",
        contract_types: "contractTypes",
        subscription_year: "subscriptionYear",
      }
      const backendErrors: Record<string, string> = {}

      if (data && typeof data === "object" && !Array.isArray(data)) {
        Object.entries(data).forEach(([field, detail]) => {
          const formField = backendFieldMap[field]
          if (!formField) return
          backendErrors[formField] = Array.isArray(detail) ? detail.join(", ") : String(detail)
        })
      }

      if (Object.keys(backendErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...backendErrors }))
        showSnackBar("Please correct the highlighted contract fields.", "error")
      } else {
        const detail = data?.detail || data?.error_summary || "Failed to save contract"
        showSnackBar(typeof detail === "string" ? detail : "Failed to save contract", "error")
      }
    }
  }

  return (
    <Modal
      open={open}
      modalHeading={isEditMode ? "Edit Contract" : "Add New Contract"}
      primaryButtonText={isSaving ? "Saving..." : isEditMode ? "Save" : "Create"}
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onSecondarySubmit={onClose}
      onRequestSubmit={handleSubmit}
      primaryButtonDisabled={isSubmitDisabled}
    >
      <p className="m-0 mb-3" style={{ fontSize: 12, color: "#5a6872" }}>
        Fields marked with <span style={{ color: "#da1e28" }}>*</span> are required.
      </p>

      <div className="mb-3">
        <RequiredFieldLabel>Name</RequiredFieldLabel>
        <TextInput
          labelText="Name"
          hideLabel
          value={name}
          onChange={e => {
            setName(e.target.value)
            markTouched("name")
            if (errors.name) setErrors(prev => ({ ...prev, name: "" }))
          }}
          placeholder="Contract name"
          invalid={!!displayedError("name")}
          invalidText={displayedError("name")}
        />
      </div>

      <div className="mb-3">
        <RequiredFieldLabel>Owner</RequiredFieldLabel>
        <TextInput
          labelText="Owner"
          hideLabel
          value={owner}
          onChange={e => {
            setOwner(e.target.value)
            markTouched("owner")
            if (errors.owner) setErrors(prev => ({ ...prev, owner: "" }))
          }}
          placeholder="Owner name"
          invalid={!!displayedError("owner")}
          invalidText={displayedError("owner")}
        />
      </div>

      <div className="mb-3">
        <div className="wfp--form-item">
          <RequiredFieldLabel>Countries or Territories</RequiredFieldLabel>
          <Select
            className="wfp--react-select-container"
            classNamePrefix="wfp--react-select"
            placeholder="Select countries or territories"
            options={countryOptions}
            isMulti
            value={countries.map(c => countryOptions.find(o => o.value === c.value) ?? c)}
            onChange={value => {
              setCountries(value as MultiValue<Option> as Option[])
              markTouched("countries")
              if (errors.countries) setErrors(prev => ({ ...prev, countries: "" }))
            }}
            styles={displayedError("countries") ? { control: base => ({ ...base, borderColor: "#da1e28" }) } : {}}
            noOptionsMessage={() => "No countries available yet"}
          />
          {displayedError("countries") && <div className="wfp--form-requirement">{displayedError("countries")}</div>}
        </div>
      </div>

      <div className="mb-3">
        <div className="wfp--form-item">
          <RequiredFieldLabel>Contract Type</RequiredFieldLabel>
          <Select
            className="wfp--react-select-container"
            classNamePrefix="wfp--react-select"
            placeholder="Select one or more types"
            options={contractTypeOptions}
            isMulti
            value={contractTypes}
            onChange={value => {
              setContractTypes(value as MultiValue<Option> as Option[])
              markTouched("contractTypes")
              if (errors.contractTypes) setErrors(prev => ({ ...prev, contractTypes: "" }))
            }}
            styles={displayedError("contractTypes") ? { control: base => ({ ...base, borderColor: "#da1e28" }) } : {}}
          />
          {displayedError("contractTypes") && (
            <div className="wfp--form-requirement">{displayedError("contractTypes")}</div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <RequiredFieldLabel>Subscription Year</RequiredFieldLabel>
        <TextInput
          labelText="Subscription Year"
          hideLabel
          disabled={hasConnectedAssets}
          value={subscriptionYear}
          onChange={e => {
            setSubscriptionYear(e.target.value)
            markTouched("subscriptionYear")
            if (errors.subscriptionYear) setErrors(prev => ({ ...prev, subscriptionYear: "" }))
          }}
          placeholder="e.g. 2024"
          invalid={!!displayedError("subscriptionYear")}
          invalidText={displayedError("subscriptionYear")}
        />
        {hasConnectedAssets && (
          <div className="wfp--form-requirement" style={{ color: "#5a6872" }}>
            Subscription year is locked because assets are connected to this contract.
          </div>
        )}
      </div>

      <div className="mb-3">
        <TextArea
          labelText="Description (optional)"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional description"
          rows={3}
        />
      </div>
    </Modal>
  )
}
